<template>
  <div class="drawer-scroll">
    <div v-if="isLoading" class="text-white p-20">Loading...</div>
    <template v-else>
      <!-- Multi-destination switcher -->
      <div v-if="destinations.length > 1" class="dest-switcher-row">
        <button
          class="dest-nav-btn"
          :disabled="currentDestinationIndex === 0"
          @click="stepDestination(-1)"
        ><i class="el-icon-arrow-left"></i></button>
        <Select
          v-model="currentDestinationIndex"
          size="small"
          class="dest-el-select"
        >
          <Option
            v-for="(dest, idx) in destinations"
            :key="idx"
            :label="getDestinationLabel(dest, idx)"
            :value="idx"
          />
        </Select>
        <span class="dest-counter"><b>{{ currentDestinationIndex + 1 }}</b>/{{ destinations.length }}</span>
        <button
          class="dest-nav-btn"
          :disabled="currentDestinationIndex === destinations.length - 1"
          @click="stepDestination(1)"
        ><i class="el-icon-arrow-right"></i></button>
      </div>
      <!-- Single destination label -->
      <div v-else-if="destinations.length === 1" class="dest-single-row">
        <span class="dest-single-label">Destination :</span>
        <span class="dest-single-name">{{ getDestinationLabel(destinations[0], 0) }}</span>
      </div>
      <div class="doc-name-row" v-if="selectedFile">
        <span class="doc-name-label">Document :</span>
        <span class="doc-name-value" :title="selectedFile.name">{{ selectedFile.name }}</span>
        <a
          v-if="selectedFile && !isImageFile(selectedFile)"
          :href="selectedFile.url"
          download
          class="doc-download-btn"
          title="Download file"
          @click.stop
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
          </svg>
        </a>
      </div>
      <div class="preview-upload" v-if="selectedFile" @click="openLightbox(selectedFile)">
        <img :src="getThumbSrc(selectedFile)" alt="" />
        <div class="preview-hover-overlay">
          <i class="el-icon-zoom-in preview-zoom-icon"></i>
        </div>
      </div>
      <div class="drawer-attachments-min">
        <AttachmentsMin
          :attachments="[]"
          :disable-upload="false"
          :multiple="true"
          :use-modal-upload="true"
          @addAttachments="$emit('add-attachment', currentDestinationIndex)"
          @openPreviewSlider="$emit('add-attachment', currentDestinationIndex)"
        />
      </div>

      <div class="drawer-scroll-body">
        <div class="doc-section-label">
          <i class="el-icon-caret-right"></i> LR DOCUMENT
          <span v-if="lrAttachmentsMapped.length" class="section-count">({{ lrAttachmentsMapped.length }})</span>
        </div>
        <div class="thumb-strip">
          <div
            v-for="file in lrAttachmentsMapped"
            :key="file.attachmentId"
            class="thumb-item"
            :class="{ 'is-selected': selectedFile && selectedFile.attachmentId === file.attachmentId }"
            :title="file.name"
            @click="selectFile(file)"
          >
            <div class="thumb-img-wrap">
              <img :src="getThumbSrc(file)" alt="" />
              <div class="thumb-hover-overlay">
                <i class="el-icon-zoom-in" @click.stop="openLightbox(file)"></i>
              </div>
              <Popconfirm
                v-if="canDeleteLr"
                title="Are you sure to delete?"
                @confirm="deleteAttachment(file.attachmentId, 1)"
              >
                <span slot="reference" class="thumb-delete" @click.stop>
                  <i class="el-icon-delete"></i>
                </span>
              </Popconfirm>
            </div>
            <div class="thumb-filename">{{ file.name }}</div>
          </div>
          <div v-if="!lrAttachmentsMapped.length" class="no-docs">No documents</div>
        </div>

        <template v-if="podAttachmentsMapped.length">
          <div class="doc-section-label mt-10">
            <i class="el-icon-caret-right"></i> POD DOCUMENT
            <span class="section-count">({{ podAttachmentsMapped.length }})</span>
          </div>
          <div class="thumb-strip">
            <div
              v-for="file in podAttachmentsMapped"
              :key="file.attachmentId"
              class="thumb-item"
              :class="{ 'is-selected': selectedFile && selectedFile.attachmentId === file.attachmentId }"
              :title="file.name"
              @click="selectFile(file)"
            >
              <div class="thumb-img-wrap">
                <img :src="getThumbSrc(file)" alt="" />
                <div class="thumb-hover-overlay">
                  <i class="el-icon-zoom-in" @click.stop="openLightbox(file)"></i>
                </div>
                <Popconfirm
                  v-if="canDeletePod"
                  title="Are you sure to delete?"
                  @confirm="deleteAttachment(file.attachmentId, 2)"
                >
                  <span slot="reference" class="thumb-delete" @click.stop>
                    <i class="el-icon-delete"></i>
                  </span>
                </Popconfirm>
              </div>
              <div class="thumb-filename">{{ file.name }}</div>
            </div>
          </div>
        </template>

        <template v-if="otherAttachmentsMapped.length">
          <div class="doc-section-label mt-10">
            <i class="el-icon-caret-right"></i> OTHER DOCUMENT
            <span class="section-count">({{ otherAttachmentsMapped.length }})</span>
          </div>
          <div class="thumb-strip">
            <div
              v-for="file in otherAttachmentsMapped"
              :key="file.attachmentId"
              class="thumb-item"
              :class="{ 'is-selected': selectedFile && selectedFile.attachmentId === file.attachmentId }"
              :title="file.name"
              @click="selectFile(file)"
            >
              <div class="thumb-img-wrap">
                <img :src="getThumbSrc(file)" alt="" />
                <div class="thumb-hover-overlay">
                  <i class="el-icon-zoom-in" @click.stop="openLightbox(file)"></i>
                </div>
                <Popconfirm
                  v-if="canDeleteOther"
                  title="Are you sure to delete?"
                  @confirm="deleteAttachment(file.attachmentId, 3)"
                >
                  <span slot="reference" class="thumb-delete" @click.stop>
                    <i class="el-icon-delete"></i>
                  </span>
                </Popconfirm>
              </div>
              <div class="thumb-filename">{{ file.name }}</div>
            </div>
          </div>
        </template>
      </div>
    </template>

    <portal to="file-preview">
      <div v-if="lightboxFile" class="doc-lightbox-portal" @click.self="closeLightbox" @wheel.prevent="onWheel">
        <div class="lightbox-toolbar">
          <button class="lb-btn" @click="rotate(-90)" title="Rotate Left">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
            </svg>
          </button>
          <button class="lb-btn" @click="rotate(90)" title="Rotate Right">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-4.5"/>
            </svg>
          </button>
          <button class="lb-btn" @click="zoom(-0.2)" title="Zoom Out"><i class="el-icon-zoom-out"></i></button>
          <span class="lb-scale">{{ Math.round(lbScale * 100) }}%</span>
          <button class="lb-btn" @click="zoom(0.2)" title="Zoom In"><i class="el-icon-zoom-in"></i></button>
          <button class="lb-btn lb-close" @click="closeLightbox" title="Close"><i class="el-icon-close"></i></button>
        </div>
        <button class="lightbox-nav lightbox-prev" @click="navigateLightbox(-1)" :disabled="lightboxIndex === 0">
          <i class="el-icon-arrow-left"></i>
        </button>
        <div
          class="lightbox-stage"
          @mousedown="onDragStart"
          @mousemove="onDragMove"
          @mouseup="onDragEnd"
          @mouseleave="onDragEnd"
        >
          <img :src="getThumbSrc(lightboxFile)" alt="" :style="imgTransformStyle" draggable="false" />
          <a
            v-if="!isImageFile(lightboxFile)"
            :href="lightboxFile.url"
            download
            class="lb-download-btn"
            title="Download"
            @click.stop
          >
            <i class="el-icon-download"></i> Download
          </a>
        </div>
        <button class="lightbox-nav lightbox-next" @click="navigateLightbox(1)" :disabled="lightboxIndex === allAttachments.length - 1">
          <i class="el-icon-arrow-right"></i>
        </button>
        <div class="lightbox-filename">{{ lightboxFile.name }}</div>
      </div>
    </portal>
  </div>
</template>

<script>
import './AttachmentPreviewDrawer.scss';
import PDF from '@common/asset/svg/pdf.svg';
import AttachmentsMin from '@common/components/AttachmentsMin';

export default {
  name: 'AttachmentPreviewDrawer',
  components: { AttachmentsMin },
  props: {
    destinations:     { type: Array,  default: () => [] },
    destinationIndex: { type: Number, default: 0 },
    indentNo:         { type: String, default: '' },
    movement:         { type: String, default: 'primary' },
  },
  data() {
    return {
      currentDestinationIndex: this.destinationIndex,
      lrAttachments:    [],
      podAttachments:   [],
      otherAttachments: [],
      isLoading:      false,
      selectedFile:   null,
      lightboxFile:   null,
      lightboxIndex:  0,
      lbScale:        1,
      lbRotation:     0,
      lbTranslateX:   0,
      lbTranslateY:   0,
      lbDragging:     false,
      lbDragStart:    { x: 0, y: 0 },
    };
  },
  computed: {
    canDeleteOther() {
      // Backend has no permission gate for other attachments (else return in _assertDeletePermission)
      // so always allow delete if user can delete LR (same role level)
      return this.$hasPermission(`transporter_delete_elr_attachments_${this.movement}`);
    },
    canDeleteLr() {
      return this.$hasPermission(`transporter_delete_elr_attachments_${this.movement}`);
    },
    canDeletePod() {
      return this.$hasPermission(`transporter_delete_pod_attachments_${this.movement}`);
    },
    lrAttachmentsMapped() {
      return this.mapAttachments(this.lrAttachments);
    },
    podAttachmentsMapped() {
      return this.mapAttachments(this.podAttachments);
    },
    otherAttachmentsMapped() {
      return this.mapAttachments(this.otherAttachments);
    },
    allAttachments() {
      return [...this.lrAttachmentsMapped, ...this.podAttachmentsMapped, ...this.otherAttachmentsMapped];
    },
    allAttachmentsMapped() {
      return [...this.lrAttachments, ...this.podAttachments, ...this.otherAttachments].map((a) => {
        // eslint-disable-next-line no-underscore-dangle
        const id = a._id;
        return {
          id,
          name:           a.content_file_name,
          url:            a.metadata && a.metadata.location,
          attachment_url: a.metadata && a.metadata.location,
        };
      });
    },
  },
  watch: {
    destinationIndex(val) {
      this.currentDestinationIndex = val;
    },
    currentDestinationIndex() {
      this.selectedFile = null;
      this.fetchAttachments();
    },
  },
  mounted() {
    this.fetchAttachments();
  },
  methods: {
    getDestinationLabel(dest, idx) {
      const name = dest.address && dest.address.name;
      const ref  = dest.address && dest.address.reference_id;
      return name ? `${name}${ref ? ` (${ref})` : ''}` : `Destination ${idx + 1}`;
    },
    onDestinationChange(event) {
      this.currentDestinationIndex = Number(event.target.value);
    },
    stepDestination(dir) {
      const next = this.currentDestinationIndex + dir;
      if (next < 0 || next >= this.destinations.length) return;
      this.currentDestinationIndex = next;
    },
    mapAttachments(list) {
      return list.map((a) => {
        // eslint-disable-next-line no-underscore-dangle
        const attachmentId = a._id;
        return {
          attachmentId,
          name:        a.content_file_name,
          url:         a.metadata && a.metadata.location,
          contentType: a.content_type,
        };
      });
    },
    getThumbSrc(file) {
      const ext = (file.name || '').split('.').pop().toLowerCase();
      const images = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      return images.includes(ext) ? file.url : PDF;
    },
    selectFile(file) {
      this.selectedFile = file;
    },
    async fetchAttachments() {
      this.isLoading = true;
      try {
        const dest = this.destinations[this.currentDestinationIndex] || {};
        const response = await this.$store.dispatch('indents/listV2Attachments', {
          indentNo:      this.indentNo,
          destinationId: dest.gate_id || dest.location_id,
        });
        const list = Array.isArray(response) ? response : (response && response.data) || [];
        const destData = list[0] || {};
        this.lrAttachments    = destData.lr_attachments    || [];
        this.podAttachments   = destData.pod_attachments   || [];
        this.otherAttachments = destData.other_attachments || [];
        if (this.lrAttachments.length) {
          this.selectedFile = this.mapAttachments(this.lrAttachments)[0];
        } else if (this.podAttachments.length) {
          this.selectedFile = this.mapAttachments(this.podAttachments)[0];
        } else if (this.otherAttachments.length) {
          this.selectedFile = this.mapAttachments(this.otherAttachments)[0];
        }
      } catch (error) {
        this.$errorMessage({ message: error.message || 'Failed to load documents' });
      } finally {
        this.isLoading = false;
      }
    },
    isImageFile(file) {
      const ext = (file.name || '').split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
    },
    openLightbox(file) {
      const idx = this.allAttachments.findIndex(a => a.attachmentId === file.attachmentId);
      this.lightboxIndex = idx >= 0 ? idx : 0;
      this.lightboxFile  = this.allAttachments[this.lightboxIndex];
      this.resetTransform();
    },
    closeLightbox() {
      this.lightboxFile = null;
      this.resetTransform();
    },
    navigateLightbox(dir) {
      const next = this.lightboxIndex + dir;
      if (next < 0 || next >= this.allAttachments.length) return;
      this.lightboxIndex = next;
      this.lightboxFile  = this.allAttachments[next];
      this.resetTransform();
    },
    resetTransform() {
      this.lbScale      = 1;
      this.lbRotation   = 0;
      this.lbTranslateX = 0;
      this.lbTranslateY = 0;
    },
    zoom(delta) {
      this.lbScale = Math.min(5, Math.max(0.2, this.lbScale + delta));
    },
    rotate(deg) {
      this.lbRotation = (this.lbRotation + deg + 360) % 360;
    },
    onWheel(e) {
      const delta = e.deltaY < 0 ? 0.15 : -0.15;
      this.zoom(delta);
    },
    onDragStart(e) {
      if (this.lbScale <= 1) return;
      this.lbDragging  = true;
      this.lbDragStart = { x: e.clientX - this.lbTranslateX, y: e.clientY - this.lbTranslateY };
    },
    onDragMove(e) {
      if (!this.lbDragging) return;
      this.lbTranslateX = e.clientX - this.lbDragStart.x;
      this.lbTranslateY = e.clientY - this.lbDragStart.y;
    },
    onDragEnd() {
      this.lbDragging = false;
    },
    async deleteAttachment(attachmentId, documentType) {
      try {
        await this.$store.dispatch('indents/deleteV2Attachment', {
          indentNo: this.indentNo,
          attachmentId,
          payload: {
            destination_id: (this.destinations[this.currentDestinationIndex] || {}).gate_id
              || (this.destinations[this.currentDestinationIndex] || {}).location_id,
            document_type:  documentType,
          },
        });
        this.$emit('deleted', { attachmentId });
        await this.fetchAttachments();
      } catch (error) {
        this.$errorMessage({ message: error.message || 'Failed to delete document' });
      }
    },
  },
};
</script>
